/**
 * CUSTOM_TAXNEXUS_BY_CLAUDE (KAREN e3166bd4c6f6 + 3b942caaed6b, Ryan 2026-10-04)
 * ==============================================================================
 * Three cards above the Tax Hub's sales report:
 *   1. Economic nexus        GET  /tax-hub/nexus          per-state sales vs each state's threshold
 *   2. Where Shopify collects GET /tax-hub/shopify-tax    registered states, red where not collecting
 *   3. Registrations & alerts GET|POST /admin/tax-nexus-config   the knobs (every knob -> portal)
 *
 * Owns everything inside #tax-nexus-root and nothing else. Own `tn-` classes on purpose: the Tax
 * Hub's collapse binder looks for the FIRST `.th-rank--collapsible`, and a card here would steal it.
 * Each card loads on its own, so one failed request shows its own error instead of a blank page.
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://rfq-worker.ryan-29b.workers.dev';
  var PREFS = 'taxnexus_collapsed_';

  var st = {
    nexus: null, nexusErr: null,
    shop: null, shopErr: null,
    cfg: null, cfgErr: null,
    showAll: false,
    draft: null,      // registrations being edited
    reg: null, regErr: null,      // CUSTOM_TAXREGISTER_BY_CLAUDE: GET /tax-hub/registrations (log + filing frequency)
    regCfg: null,                 // GET /admin/tax-registrations-config (the expiry reminder switch)
    saving: false, saveMsg: '', saveErr: false
  };

  function api(path, opts) {
    if (!(window.RepPortal && window.RepPortal.API && window.RepPortal.API.fetchExternal)) {
      return Promise.reject(new Error('Portal API helper unavailable. Reload the page.'));
    }
    return window.RepPortal.API.fetchExternal(WORKER_URL, path, opts || { method: 'GET' });
  }
  function session() {
    if (window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession) return window.RepPortal.Auth.getSession();
    try { var raw = sessionStorage.getItem('rep_portal_session') || localStorage.getItem('rep_portal_session'); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }
  function usd(n) { return '$' + Math.round(Number(n) || 0).toLocaleString('en-US'); }
  function collapsed(id) { try { return localStorage.getItem(PREFS + id) === '1'; } catch (e) { return false; } }
  function setCollapsed(id, v) { try { localStorage.setItem(PREFS + id, v ? '1' : '0'); } catch (e) {} }
  function rootEl() { return document.getElementById('tax-nexus-root'); }

  var PERIOD_WORDS = { 'cy-or-py': 'last or this calendar year', 'py': 'last calendar year', 'r12': 'rolling 12 months', 'sep30': '12 months to Sep 30' };
  var KIND_WORDS = { sales: 'Sales tax permit', use: 'Use tax permit', reseller: 'Reseller permit', other: 'Other registration' };

  function card(id, title, peek, body) {
    var c = collapsed(id);
    return '<section class="tn-card' + (c ? ' tn-card--collapsed' : '') + '" data-tn-card="' + id + '">'
      + '<div class="tn-card__head" role="button" tabindex="0" aria-expanded="' + (c ? 'false' : 'true') + '">'
      + '<h2 class="tn-card__title">' + title + '</h2>'
      + '<span class="tn-card__peek">' + peek + '</span>'
      + '<span class="tn-card__chev" aria-hidden="true">' + (c ? '+' : '&minus;') + '</span>'
      + '</div><div class="tn-card__body">' + body + '</div></section>';
  }

  /* ── 1. nexus ─────────────────────────────────────────────────────────────── */
  function testWords(t) {
    if (!t) return '&mdash;';
    if (t.noStateTax) return 'No state sales tax';
    var s = usd(t.dollars);
    if (t.txns) s += (t.txn === 'and' ? ' and ' : ' or ') + t.txns + ' orders';
    return s;
  }
  function pill(s) {
    if (s.level === 'none') return '<span class="tn-pill tn-pill--none">No sales tax</span>';
    if (s.level === 'crossed') return '<span class="tn-pill ' + (s.covered ? 'tn-pill--covered">Crossed &middot; registered' : 'tn-pill--bad">Crossed') + '</span>';
    if (s.level === 'warn') return '<span class="tn-pill ' + (s.covered ? 'tn-pill--covered' : 'tn-pill--warn') + '">' + s.percent + '%</span>';
    return '<span class="tn-pill tn-pill--ok">' + (s.percent || 0) + '%</span>';
  }
  function nexusCard() {
    if (st.nexusErr) return card('nexus', 'Economic nexus', 'failed to load', '<p class="tn-err">' + esc(st.nexusErr) + '</p>');
    if (!st.nexus) return card('nexus', 'Economic nexus', 'loading&hellip;', '<p class="tn-muted">Building the report (a few seconds)&hellip;</p>');
    var d = st.nexus;
    var crossed = d.summary.crossed.length, warn = d.summary.warn.length, alerting = d.summary.alerting.length;
    var peek = crossed + ' crossed &middot; ' + warn + ' at 80%+ &middot; ' + alerting + ' to act on'
      + (d.thresholdsReviewed ? '' : ' &middot; <span class="tn-warnword">thresholds not yet reviewed</span>');
    var rows = d.states.filter(function (s) {
      if (st.showAll) return true;
      return s.level === 'crossed' || s.level === 'warn' || (s.totals && (s.totals.r12.orders || s.totals.cy.orders || s.totals.py.orders));
    });
    var h = '<p class="tn-muted">' + esc(d.measure) + ' Warning at 80%. A state with an active sales-tax permit on file is shown but never alerts. '
      + 'Thresholds are a starting table from published state rules (as of ' + esc(d.thresholdsAsOf) + ')'
      + (d.thresholdsReviewed ? ', reviewed by ' + esc(d.thresholdsReviewed.by) + ' on ' + esc(String(d.thresholdsReviewed.at).slice(0, 10)) : ', not yet reviewed by Karen') + '.</p>';
    h += '<div class="tn-tablewrap"><table class="tn-table"><thead><tr><th>State</th><th>Threshold</th><th>Measured over</th>'
      + '<th class="tn-num">Rolling 12</th><th class="tn-num">' + esc(d.windows.cy.label) + '</th><th class="tn-num">' + esc(d.windows.py.label) + '</th>'
      + '<th>Status</th><th>On file</th></tr></thead><tbody>';
    if (!rows.length) h += '<tr><td colspan="8" class="tn-muted">No sales in any state in these windows.</td></tr>';
    rows.forEach(function (s) {
      var t = s.totals;
      var cell = function (x) { return usd(x.sales) + '<span class="tn-sub">' + x.orders + ' order' + (x.orders === 1 ? '' : 's') + (x.taxCollected ? ' &middot; ' + usd(x.taxCollected) + ' tax' : '') + '</span>'; };
      var reg = s.registration ? esc(KIND_WORDS[s.registration.kind] || 'Registration') + (s.registration.status !== 'active' ? ' (' + esc(s.registration.status) + ')' : '') + (s.registration.last4 ? ' &middot; &hellip;' + esc(s.registration.last4) : '') : '<span class="tn-muted">&mdash;</span>';
      h += '<tr class="' + (s.alerting ? 'tn-row--alert' : '') + '"><td><strong>' + esc(s.code) + '</strong> <span class="tn-sub tn-inline">' + esc(s.name) + '</span></td>'
        + '<td>' + testWords(s.threshold) + (s.threshold && s.threshold.overridden ? ' <span class="tn-sub tn-inline">(override)</span>' : '') + '</td>'
        + '<td>' + (s.threshold && !s.threshold.noStateTax ? esc(PERIOD_WORDS[s.threshold.period] || s.threshold.period) : '&mdash;') + '</td>'
        + '<td class="tn-num">' + cell(t.r12) + '</td><td class="tn-num">' + cell(t.cy) + '</td><td class="tn-num">' + cell(t.py) + '</td>'
        + '<td>' + pill(s) + (s.bindsFrom ? '<span class="tn-sub">binds from ' + esc(s.bindsFrom) + '</span>' : '') + '</td><td>' + reg + '</td></tr>';
    });
    h += '</tbody></table></div>';
    h += '<button type="button" class="th-btn th-btn--sm" data-tn="showall">' + (st.showAll ? 'Show only states with sales' : 'Show all 51') + '</button>';
    if (d.unassignedRows) h += ' <span class="tn-muted">' + d.unassignedRows + ' sale(s) had no state and are counted nowhere.</span>';
    return card('nexus', 'Economic nexus', peek, h);
  }

  /* ── 2. where Shopify collects ────────────────────────────────────────────── */
  function shopCard() {
    if (st.shopErr) return card('shop', 'Where Shopify is collecting', 'failed to load', '<p class="tn-err">' + esc(st.shopErr) + '</p>');
    if (!st.shop) return card('shop', 'Where Shopify is collecting', 'loading&hellip;', '<p class="tn-muted">Reading online orders&hellip;</p>');
    var d = st.shop;
    var peek = (d.red.length ? '<span class="tn-badword">' + d.red.length + ' red: ' + esc(d.red.join(', ')) + '</span>' : 'no red flags')
      + (d.amber.length ? ' &middot; ' + d.amber.length + ' amber' : '')
      + ' &middot; collecting seen in ' + (d.collectingSeen.length ? esc(d.collectingSeen.join(', ')) : 'none');
    var h = '<p class="tn-muted">Judged from what online orders were actually charged in the last ' + d.observed.days
      + ' days (Shopify does not let apps read the on/off setting itself). Red = registered and not seen collecting. Amber = tax charged with no registration on file.</p>';
    h += '<div class="tn-tablewrap"><table class="tn-table"><thead><tr><th>State</th><th>On file</th><th class="tn-num">Online orders</th><th class="tn-num">Taxed</th><th>Last taxed</th><th>Flag</th></tr></thead><tbody>';
    d.flags.forEach(function (f) {
      var o = f.observed || {};
      h += '<tr class="tn-row--' + f.flag + '"><td><strong>' + esc(f.state) + '</strong></td>'
        + '<td>' + esc(f.registrationLabel) + (f.registration && f.registration.last4 ? ' &middot; &hellip;' + esc(f.registration.last4) : '') + '</td>'
        + '<td class="tn-num">' + (o.orders || 0) + '</td><td class="tn-num">' + (o.taxed || 0) + '</td>'
        + '<td>' + esc(o.lastTaxed || '—') + '</td>'
        + '<td><span class="tn-pill tn-pill--' + (f.flag === 'red' ? 'bad' : f.flag === 'amber' ? 'warn' : 'ok') + '">' + esc(f.flag) + '</span><span class="tn-sub">' + esc(f.why) + '</span></td></tr>';
    });
    h += '</tbody></table></div>';
    if (d.shop && d.shop.available) h += '<p class="tn-muted">Shopify: prices ' + (d.shop.taxesIncluded ? 'include' : 'do not include') + ' tax &middot; tax on shipping ' + (d.shop.taxShipping ? 'on' : 'off') + '.</p>';
    return card('shop', 'Where Shopify is collecting', peek, h);
  }

  /* ── 3. registrations & alerts ────────────────────────────────────────────── */
  function regRow(r, i) {
    var kinds = Object.keys(KIND_WORDS).map(function (k) { return '<option value="' + k + '"' + (r.kind === k ? ' selected' : '') + '>' + KIND_WORDS[k] + '</option>'; }).join('');
    var stats = ['active', 'expired', 'pending'].map(function (k) { return '<option value="' + k + '"' + (r.status === k ? ' selected' : '') + '>' + k + '</option>'; }).join('');
    return '<tr data-tn-reg="' + i + '">'
      + '<td><input class="tn-in tn-in--st" data-f="state" maxlength="2" value="' + esc(r.state) + '" aria-label="State"></td>'
      + '<td><select class="tn-in" data-f="kind">' + kinds + '</select></td>'
      + '<td><input class="tn-in tn-in--l4" data-f="last4" maxlength="4" value="' + esc(r.last4 || '') + '" placeholder="last 4" aria-label="Last 4 of ID"></td>'
      + '<td><select class="tn-in" data-f="status">' + stats + '</select></td>'
      + '<td><input class="tn-in" type="date" data-f="issued" value="' + esc(r.issued || '') + '" aria-label="Issued"></td>'
      + '<td><input class="tn-in" type="date" data-f="expires" value="' + esc(r.expires || '') + '" aria-label="Expires"></td>'
      + '<td><input class="tn-in" type="date" data-f="registeredOn" value="' + esc(r.registeredOn || '') + '" aria-label="Registered on"></td>'
      + '<td class="tn-muted-cell">' + esc(freqWords(r)) + '</td>'
      + '<td><input class="tn-in tn-in--note" data-f="note" value="' + esc(r.note || '') + '" aria-label="Note"></td>'
      + '<td><button type="button" class="th-btn th-btn--sm" data-tn="rm" data-i="' + i + '" aria-label="Remove">&times;</button></td></tr>';
  }
  /* CUSTOM_TAXREGISTER_BY_CLAUDE (KAREN 4558d7069421) - filing frequency is the filing calendar's, shown read-only here. */
  var FREQ_WORDS = { monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Yearly' };
  function freqWords(r) {
    var rows = (st.reg && st.reg.rows) || [];
    for (var i = 0; i < rows.length; i++) if (rows[i].state === r.state && rows[i].kind === r.kind) return FREQ_WORDS[rows[i].filingFrequency] || '\u2014';
    return '\u2014';
  }
  var FIELD_WORDS = { last4: 'last 4', status: 'status', issued: 'issued', expires: 'expires', registeredOn: 'registered on', note: 'note' };
  function changeLine(e) {
    var what = e.action === 'added' ? 'added' : e.action === 'removed' ? 'removed' : Object.keys(e.fields || {}).map(function (f) {
      var v = e.fields[f]; return (FIELD_WORDS[f] || f) + ' ' + esc(v[0] == null ? '(blank)' : v[0]) + ' &rarr; ' + esc(v[1] == null ? '(blank)' : v[1]);
    }).join(', ');
    return '<li><strong>' + esc(e.state) + '</strong> ' + esc(KIND_WORDS[e.kind] || e.kind) + ': ' + what
      + ' <span class="tn-sub tn-inline">' + esc(e.by || '') + ' &middot; ' + esc(String(e.at || '').slice(0, 10)) + '</span></li>';
  }
  function regExtras() {
    var on = !!(st.regCfg && st.regCfg.config && st.regCfg.config.remindersEnabled);
    var h = '<label class="tn-check"><input type="checkbox" data-tn="regremind"' + (on ? ' checked' : '') + '> '
      + '<span><strong>Permit expiry reminders</strong>: wake Karen once 60 days, once 30 days and once 7 days before an active or pending permit expires.</span></label>';
    if (st.regErr) h += '<p class="tn-err">' + esc(st.regErr) + '</p>';
    var log = (st.reg && st.reg.log) || [];
    if (log.length) h += '<p class="tn-muted"><strong>Recent changes</strong></p><ul class="tn-log">' + log.slice(0, 8).map(changeLine).join('') + '</ul>';
    return h;
  }
  function cfgCard() {
    if (st.cfgErr) return card('cfg', 'Registrations &amp; alerts', 'failed to load', '<p class="tn-err">' + esc(st.cfgErr) + '</p>');
    if (!st.cfg) return card('cfg', 'Registrations &amp; alerts', 'loading&hellip;', '');
    var c = st.cfg.config;
    if (!st.draft) st.draft = c.registrations.map(function (r) { return Object.assign({}, r); });
    var peek = 'Alerts ' + (c.alertsEnabled ? '<span class="tn-okword">ON</span>' : 'OFF') + ' &middot; ' + c.registrations.length + ' registration' + (c.registrations.length === 1 ? '' : 's');
    var last = st.cfg.lastRun;
    var h = '<label class="tn-check"><input type="checkbox" data-tn="alerts"' + (c.alertsEnabled ? ' checked' : '') + '> '
      + '<span><strong>Nexus alerts</strong>: each morning, wake Karen once when a state we hold no active sales-tax permit in crosses its threshold, and once when it reaches 80%.</span></label>';
    h += '<label class="tn-check"><input type="checkbox" data-tn="exempt"' + (c.excludeExempt ? ' checked' : '') + '> '
      + '<span><strong>Leave out tax-exempt sales</strong> from the nexus totals. There are no exemption records yet, so this changes nothing today.</span></label>';
    h += '<p class="tn-muted">Thresholds: ' + (c.reviewedBy ? 'reviewed by ' + esc(c.reviewedBy) + ' on ' + esc(String(c.reviewedAt).slice(0, 10)) : 'not yet reviewed')
      + ' <button type="button" class="th-btn th-btn--sm" data-tn="reviewed">Mark thresholds reviewed</button></p>';
    h += '<div class="tn-tablewrap"><table class="tn-table tn-table--edit"><thead><tr><th>State</th><th>Kind</th><th>Last 4</th><th>Status</th><th>Issued</th><th>Expires</th><th>Registered on</th><th>Files</th><th>Note</th><th></th></tr></thead><tbody>'
      + st.draft.map(regRow).join('') + '</tbody></table></div>';
    h += '<p class="tn-muted">Only the last 4 characters of an ID are ever stored. Only an active <em>sales tax permit</em> stops a state from alerting. '
      + '<em>Registered on</em> is the first day we were registered; the wrong-tax order check reads it. <em>Files</em> comes from the filing calendar.</p>';
    h += '<div class="tn-actions"><button type="button" class="th-btn th-btn--sm" data-tn="add">+ Add registration</button>'
      + '<button type="button" class="th-btn th-btn--primary" data-tn="save"' + (st.saving ? ' disabled' : '') + '>' + (st.saving ? 'Saving&hellip;' : 'Save') + '</button>'
      + (st.saveMsg ? '<span class="' + (st.saveErr ? 'tn-err' : 'tn-okword') + '">' + esc(st.saveMsg) + '</span>' : '') + '</div>';
    h += regExtras();
    if (last) h += '<p class="tn-muted">Last alert check: ' + esc(String(last.at).slice(0, 16).replace('T', ' ')) + ' UTC'
      + (last.wake ? ' &middot; woke ' + esc(last.wake.seat) + ' (' + esc(last.wake.event) + ')' : ' &middot; nothing new') + '.</p>';
    return card('cfg', 'Registrations &amp; alerts', peek, h);
  }

  /* ── render + events ──────────────────────────────────────────────────────── */
  function render() {
    var el = rootEl();
    if (!el) return;
    el.innerHTML = nexusCard() + shopCard() + cfgCard();
  }
  function readDraft() {
    var rows = document.querySelectorAll('[data-tn-reg]');
    st.draft = Array.prototype.map.call(rows, function (tr) {
      var o = {};
      tr.querySelectorAll('[data-f]').forEach(function (inp) { o[inp.getAttribute('data-f')] = inp.value.trim(); });
      o.state = (o.state || '').toUpperCase();
      if (!o.last4) o.last4 = null; if (!o.expires) o.expires = null;
      if (!o.issued) o.issued = null; if (!o.registeredOn) o.registeredOn = null;
      return o;
    });
  }
  function save(body, msg) {
    st.saving = true; st.saveMsg = ''; render();
    api('/admin/tax-nexus-config', { method: 'POST', body: JSON.stringify(body) }).then(function (res) {
      st.saving = false; st.saveErr = false; st.saveMsg = msg;
      st.cfg.config = res.config; st.draft = null;
      render(); loadNexus(); loadShop(); loadReg();
    }).catch(function (err) {
      st.saving = false; st.saveErr = true; st.saveMsg = 'Save failed: ' + ((err && err.message) || 'request failed');
      render();
    });
  }
  function onClick(e) {
    var head = e.target.closest('.tn-card__head');
    if (head) {
      var cardEl = head.parentElement; var id = cardEl.getAttribute('data-tn-card');
      var now = cardEl.classList.toggle('tn-card--collapsed'); setCollapsed(id, now);
      head.setAttribute('aria-expanded', now ? 'false' : 'true');
      head.querySelector('.tn-card__chev').innerHTML = now ? '+' : '&minus;';
      return;
    }
    var b = e.target.closest('[data-tn]');
    if (!b) return;
    var what = b.getAttribute('data-tn');
    if (what === 'showall') { st.showAll = !st.showAll; render(); }
    else if (what === 'add') { readDraft(); st.draft.push({ state: '', kind: 'sales', last4: null, status: 'active', issued: null, expires: null, registeredOn: null, note: '' }); render(); }
    else if (what === 'rm') { readDraft(); st.draft.splice(Number(b.getAttribute('data-i')), 1); render(); }
    else if (what === 'save') { readDraft(); save({ registrations: st.draft }, 'Saved.'); }
    else if (what === 'reviewed') { save({ markReviewed: true }, 'Thresholds marked reviewed.'); }
  }
  function onChange(e) {
    var b = e.target.closest('[data-tn]');
    if (!b) return;
    var what = b.getAttribute('data-tn');
    if (what === 'alerts') save({ alertsEnabled: b.checked }, b.checked ? 'Nexus alerts are ON.' : 'Nexus alerts are OFF.');
    if (what === 'exempt') save({ excludeExempt: b.checked }, 'Saved.');
    if (what === 'regremind') saveRegCfg(b.checked);
  }
  function onKey(e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.classList && e.target.classList.contains('tn-card__head')) { e.preventDefault(); e.target.click(); }
  }

  function errOf(err) { var m = (err && err.message) || 'Request failed.'; return /403|admins only/i.test(m) ? 'Admins only.' : m; }
  function loadNexus() { api('/tax-hub/nexus').then(function (d) { st.nexus = d; st.nexusErr = null; render(); }).catch(function (e) { st.nexusErr = errOf(e); render(); }); }
  function loadShop() { api('/tax-hub/shopify-tax').then(function (d) { st.shop = d; st.shopErr = null; render(); }).catch(function (e) { st.shopErr = errOf(e); render(); }); }
  function loadReg() { api('/tax-hub/registrations').then(function (d) { st.reg = d; st.regErr = null; render(); }).catch(function (e) { st.regErr = errOf(e); render(); }); }
  function loadRegCfg() { api('/admin/tax-registrations-config').then(function (d) { st.regCfg = d; render(); }).catch(function () {}); }
  function saveRegCfg(on) {
    api('/admin/tax-registrations-config', { method: 'POST', body: JSON.stringify({ remindersEnabled: !!on }) }).then(function (res) {
      st.regCfg = { config: res.config }; st.saveErr = false; st.saveMsg = on ? 'Permit expiry reminders are ON.' : 'Permit expiry reminders are OFF.'; render();
    }).catch(function (err) { st.saveErr = true; st.saveMsg = 'Save failed: ' + ((err && err.message) || 'request failed'); render(); });
  }
  function loadCfg() { api('/admin/tax-nexus-config').then(function (d) { st.cfg = d; st.cfgErr = null; render(); }).catch(function (e) { st.cfgErr = errOf(e); render(); }); }

  var booted = false;
  function boot() {
    if (booted) return;
    var s = session();
    if (!s) return;
    booted = true;
    var el = rootEl();
    if (!el) return;
    if (!s.admin) { el.hidden = true; return; }
    el.addEventListener('click', onClick);
    el.addEventListener('change', onChange);
    el.addEventListener('keydown', onKey);
    render();
    loadNexus(); loadShop(); loadCfg(); loadReg(); loadRegCfg();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('rep-portal:authenticated', boot);
  window.TaxNexus = { state: st, reload: function () { loadNexus(); loadShop(); loadCfg(); loadReg(); loadRegCfg(); } };
})();
